import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import Layout from './shared/components/Layout';
import ProtectedRoute from './shared/components/ProtectedRoute';
import DevToolsFloatingButton from './components/DevToolsFloatingButton';
import { lazyRoute } from './shared/components/lazyRoute';

// Lazy load pages
const Home = lazyRoute(() => import('./pages/Home'));
const MemberListPage = lazyRoute(() => import('./pages/members/MemberListPage'));
const MemberConsumption = lazyRoute(() => import('./pages/members/MemberConsumption'));
const MemberDetailPage = lazyRoute(() => import('./pages/members/MemberDetailPage'));
const CheckoutPage = lazyRoute(() => import('./pages/members/CheckoutPage'));
const EmployeeManagementPage = lazyRoute(() => import('./pages/employees/EmployeeManagementPage'));
const EmployeeFormPage = lazyRoute(() => import('./pages/employees/EmployeeFormPage'));
const AttendancePage = lazyRoute(() => import('./pages/employees/AttendancePage'));
const SchedulePage = lazyRoute(() => import('./pages/employees/SchedulePage'));
const SalaryPage = lazyRoute(() => import('./pages/employees/SalaryPage'));
const MenuManagementPage = lazyRoute(() => import('./pages/restaurant/MenuManagementPage'));
const RestaurantManagementPage = lazyRoute(
    () => import('./pages/restaurant/RestaurantManagementPage')
);
const OrderManagementPage = lazyRoute(() => import('./pages/restaurant/OrderManagementPage'));
// Course Feature Module
const ReservationManagementPage = lazyRoute(
    () => import('./features/course/pages/ReservationManagementPage')
);
const CourseMaintenancePage = lazyRoute(
    () => import('./features/course/pages/CourseMaintenancePage')
);
const HoleInfoPage = lazyRoute(() => import('./features/course/pages/HoleInfoPage'));
const RevenueManagementPage = lazyRoute(() => import('./pages/finance/RevenueManagementPage'));
const TeeSheetPage = lazyRoute(() => import('./pages/teegle/TeeSheetPage'));
const TodayPlayRecords = lazyRoute(() => import('./pages/playRecords/TodayPlayRecords'));
const CourseSettingsPage = lazyRoute(() => import('./pages/settings/CourseSettingsPage'));
const CourseManagementPage = lazyRoute(() => import('./pages/settings/CourseManagementPage'));
const ReservationSlotSettingsPage = lazyRoute(
    () => import('./pages/settings/ReservationSlotSettingsPage')
);
const CourseProfilePage = lazyRoute(() => import('./pages/settings/CourseProfilePage'));
const FacilityStatusPage = lazyRoute(() => import('./pages/settings/FacilityStatusPage'));
const LockerManagementPage = lazyRoute(() => import('./pages/settings/LockerManagementPage'));

const RolePermissionsPage = lazyRoute(() => import('./pages/settings/RolePermissionsPage'));
const AuditLogPage = lazyRoute(() => import('./pages/settings/AuditLogPage'));
// 開發工具只存在於開發建置；正式建置連同它的 API 呼叫都不會被打包
const DevToolsPage = import.meta.env.DEV ? lazy(() => import('./pages/dev/DevToolsPage')) : null;
const PunchClockPage = lazyRoute(() => import('./pages/punch/PunchClockPage'));
const ConsumableManagementPage = lazyRoute(
    () => import('./pages/consumable/ConsumableManagementPage')
);
const ShopPage = lazyRoute(() => import('./pages/shop/ShopPage'));
const ShopManagementPage = lazyRoute(() => import('./pages/shop/ShopManagementPage'));
const ShopProductManagementPage = lazyRoute(() => import('./pages/shop/ShopProductManagementPage'));
const ShopCategoryManagementPage = lazyRoute(
    () => import('./pages/shop/ShopCategoryManagementPage')
);
const ShopInventorySummaryPage = lazyRoute(
    () => import('./pages/inventory/ShopInventorySummaryPage')
);
const AssetMaintenancePage = lazyRoute(() => import('./pages/asset/AssetMaintenancePage'));
const LoginPage = lazyRoute(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazyRoute(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyRoute(() => import('./pages/auth/ResetPasswordPage'));
const NotFoundPage = lazyRoute(() => import('./pages/NotFoundPage'));

const LoadingFallback = () => (
    <div className="flex min-h-[60vh] items-center justify-center">
        <span className="loading loading-spinner loading-lg text-primary" />
    </div>
);

function App() {
    return (
        <>
            {/* 建置期常數：正式建置會整段移除，不會再向後端詢問環境 */}
            {import.meta.env.DEV && <DevToolsFloatingButton />}
            <Suspense fallback={<LoadingFallback />}>
                <Routes>
                    {/* Auth routes (without Layout) */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />

                    {/* Main routes (with Layout) */}
                    <Route element={<Layout />}>
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <Home />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/members"
                            element={
                                <ProtectedRoute requirePermission="members:read">
                                    <MemberListPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/members/:id"
                            element={
                                <ProtectedRoute requirePermission="members:read">
                                    <MemberDetailPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/members/consumption"
                            element={
                                <ProtectedRoute requirePermission="members:read">
                                    <MemberConsumption />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/checkout"
                            element={
                                <ProtectedRoute requirePermission="checkout:process">
                                    <CheckoutPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/employees"
                            element={
                                <ProtectedRoute requirePermission="employees:read">
                                    <EmployeeManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/employees/new"
                            element={
                                <ProtectedRoute requirePermission="employees:write">
                                    <EmployeeFormPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/employees/edit/:id"
                            element={
                                <ProtectedRoute requirePermission="employees:write">
                                    <EmployeeFormPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/attendance"
                            element={
                                <ProtectedRoute requirePermission="attendance:read">
                                    <AttendancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/schedule"
                            element={
                                <ProtectedRoute requirePermission="schedule:read">
                                    <SchedulePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/salary"
                            element={
                                <ProtectedRoute requirePermission="salary:read">
                                    <SalaryPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/menu"
                            element={
                                <ProtectedRoute requirePermission="menu:read">
                                    <MenuManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/restaurants"
                            element={
                                <ProtectedRoute requirePermission="menu:read">
                                    <RestaurantManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/orders"
                            element={
                                <ProtectedRoute requirePermission="orders:read">
                                    <OrderManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reservations"
                            element={
                                <ProtectedRoute requirePermission="reservations:read">
                                    <ReservationManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/maintenance"
                            element={
                                <ProtectedRoute requirePermission="maintenance:read">
                                    <CourseMaintenancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/holes"
                            element={
                                <ProtectedRoute requirePermission="holes:read">
                                    <HoleInfoPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/revenue"
                            element={
                                <ProtectedRoute requirePermission="revenue:read">
                                    <RevenueManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/teesheet"
                            element={
                                <ProtectedRoute requirePermission="teesheet:read">
                                    <TeeSheetPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/play/today"
                            element={
                                <ProtectedRoute requirePermission="playRecords:read">
                                    <TodayPlayRecords />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/course"
                            element={
                                <ProtectedRoute>
                                    <CourseSettingsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/courses"
                            element={
                                <ProtectedRoute>
                                    <CourseManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/reservation-slots"
                            element={
                                <ProtectedRoute>
                                    <ReservationSlotSettingsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/course-profile"
                            element={
                                <ProtectedRoute requirePermission="courseProfile:read">
                                    <CourseProfilePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/facilities"
                            element={
                                <ProtectedRoute requirePermission="facility:read">
                                    <FacilityStatusPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/lockers"
                            element={
                                <ProtectedRoute requirePermission="locker:read">
                                    <LockerManagementPage />
                                </ProtectedRoute>
                            }
                        />

                        <Route
                            path="/settings/roles"
                            element={
                                <ProtectedRoute requirePermission="employees:write">
                                    <RolePermissionsPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings/audit-logs"
                            element={
                                <ProtectedRoute requirePermission="auditLogs:read">
                                    <AuditLogPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/consumables"
                            element={
                                <ProtectedRoute requirePermission="consumable:read">
                                    <ConsumableManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/asset-maintenance"
                            element={
                                <ProtectedRoute requirePermission="asset:read">
                                    <AssetMaintenancePage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/shop"
                            element={
                                <ProtectedRoute requirePermission="consumption:write">
                                    <ShopPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/shop/management"
                            element={
                                <ProtectedRoute requirePermission="shop:write">
                                    <ShopManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/shop/products"
                            element={
                                <ProtectedRoute requirePermission="shop:write">
                                    <ShopProductManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/shop/categories"
                            element={
                                <ProtectedRoute requirePermission="shop:write">
                                    <ShopCategoryManagementPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/inventory/shop"
                            element={
                                <ProtectedRoute requirePermission="shop:read">
                                    <ShopInventorySummaryPage />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/punch-clock"
                            element={
                                <ProtectedRoute>
                                    <PunchClockPage />
                                </ProtectedRoute>
                            }
                        />
                        {/* Dev tools - no auth required, only available in dev mode */}
                        {DevToolsPage && <Route path="/dev-tools" element={<DevToolsPage />} />}
                    </Route>

                    {/* 404 page */}
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
            </Suspense>
        </>
    );
}

export default App;
